import { Skeleton } from "@flat-ui";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { SphereSupportLink } from "@components/common/sphere-support-link";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Box } from "@mui-wrapper";
import { ErrorPageBase } from "@pages/error-page-base";
import { runtimeConfig } from "@src/runtime-config";
import { useCompanyId } from "@context-providers/company-id-provider";
import NotFoundIcon from "@assets/illustrations/page-not-found_566px.svg";
import { CommonStyles } from "@styles/common-styles";
import { models } from "powerbi-client";
import { PowerBIEmbed } from "powerbi-client-react";
import { useEffect, useState } from "react";
import { ContactSupportLocations } from "@custom-types/types";

/**
 * The (human-readable) names of the pages of the Premium Analytics reports
 * Don't change the name as the pages are already defined in the Power BI app
 */
export let PremiumAnalyticsPages = /*#__PURE__*/function (PremiumAnalyticsPages) {
  PremiumAnalyticsPages["projects"] = "Projects";
  PremiumAnalyticsPages["user"] = "User";
  PremiumAnalyticsPages["groups"] = "Groups";
  return PremiumAnalyticsPages;
}({});
/** Embeds premium analytics page using Power BI */
export function PowerBIAnalytics({
  pageDisplayName
}) {
  const companyId = useCompanyId();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const [isLoading, setIsLoading] = useState(true);
  const [powerBIMeta, setPowerBIMeta] = useState(null);
  const [pageName, setPageName] = useState(null);

  // Fetch Power BI meta data to embed in premium analytics page
  useEffect(() => {
    async function fetchPowerBIMeta() {
      try {
        setIsLoading(true);
        const {
          token
        } = await faroCoreApiClient.analytics.getTokenForCompanyReportMeta(companyId);
        const companyReportMeta = await faroCoreApiClient.analytics.getCompanyReportMeta({
          companyId,
          jwtToken: token,
          powerBIUrl: runtimeConfig.urls.powerBIBaseUrl
        });
        setPowerBIMeta(companyReportMeta);

        // Set the page name
        const page_0 = companyReportMeta.pages.find(page => page.displayName === pageDisplayName);
        const name = page_0 ? page_0.name : companyReportMeta.pages[0].name;
        setPageName(name);
      } catch (error) {
        handleErrorWithToast({
          id: `fetchPower BICompanyReport-${Date.now().toString()}`,
          title: "Error fetching analytics data",
          error
        });
      } finally {
        setIsLoading(false);
      }
    }
    void fetchPowerBIMeta();
  }, [companyId, handleErrorWithToast, pageDisplayName]);
  return <Box component="div" sx={{
    marginTop: "12px",
    "& .Power_Bi_Embedded": {
      height: "100%"
    },
    "& .Power_Bi_Embedded iframe": {
      border: CommonStyles.Borders.divider
    },
    width: "100%",
    height: "80vh"
  }}>
      {/* Loading skeleton */}
      {isLoading && <Skeleton variant="rectangular" sx={{
      width: "100%",
      height: "100%"
    }} />}

      {/* Analytics */}
      {!isLoading && powerBIMeta && pageName && <PowerBIEmbed embedConfig={{
      type: "report",
      id: powerBIMeta.reportId,
      tokenType: models.TokenType.Embed,
      embedUrl: powerBIMeta.embedUrl,
      accessToken: powerBIMeta.embedToken,
      pageName,
      filters: [],
      settings: {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Defined in the Power BI settings
        filterPaneEnabled: false,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Defined in the Power BI settings
        navContentPaneEnabled: false
      }
    }} cssClassName="Power_Bi_Embedded" />}

      {/* No data to display */}
      {!isLoading && (!powerBIMeta || !pageName) && <ErrorPageBase title="Premium analytics could not be loaded" shouldShowSupportBtn={true} iconImg={NotFoundIcon}>
          There is an error while loading premium analytics. Please contact
          support for assistance.
          <SphereSupportLink location={ContactSupportLocations.premiumAnalytics} />
        </ErrorPageBase>}
    </Box>;
}